"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { AlertDialog as RadixAlertDialog, Avatar as RadixAvatar, Badge as RadixBadge, Button as RadixButton, Dialog as RadixDialog, DropdownMenu as RadixDropdownMenu, IconButton as RadixIconButton, Select as RadixSelect, Skeleton as RadixSkeleton, Text, TextField as RadixTextField, } from "@radix-ui/themes";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import cx from "clsx";
import { useElement } from "./widgets-context";
import { namespaceClassNames } from "./utils";
import { useLayoutEffect } from "./use-layout-effect";
import { EyeClosedIcon, EyeOpenIcon } from "@radix-ui/react-icons";
export const PrimaryButton = React.forwardRef(function PrimaryButton({ className, ...props }, ref) {
    const element = useElement("primaryButton");
    return (_jsx(RadixButton, { ref: ref, className: cx(namespaceClassNames("button", "button--primary"), className), variant: "solid", ...props, ...element }));
});
export const SecondaryButton = React.forwardRef(function SecondaryButton({ className, ...props }, ref) {
    const element = useElement("secondaryButton");
    return (_jsx(RadixButton, { ref: ref, highContrast: true, className: cx(namespaceClassNames("button", "button--secondary"), className), variant: "surface", color: "gray", ...props, ...element }));
});
export const DestructiveButton = React.forwardRef(function DestructiveButton({ className, ...props }, ref) {
    const element = useElement("destructiveButton");
    return (_jsx(RadixButton, { ref: ref, className: cx(namespaceClassNames("button", "button--destructive"), className), variant: "solid", color: "red", ...props, ...element }));
});
export const IconButton = React.forwardRef(function IconButton({ className, ...props }, ref) {
    const element = useElement("iconButton");
    return (_jsx(RadixIconButton, { ref: ref, className: cx(namespaceClassNames("button", "icon-button"), className), variant: "ghost", color: "gray", ...props, ...element }));
});
export const Skeleton = React.forwardRef(function Skeleton({ className, ...props }, ref) {
    const element = useElement("skeleton");
    return (_jsx(RadixSkeleton, { ref: ref, className: cx(namespaceClassNames("skeleton"), className), ...props, ...element }));
});
export const SelectTrigger = React.forwardRef(function SelectTrigger({ className, ...props }, ref) {
    const element = useElement("select");
    return (_jsx(RadixSelect.Trigger, { ref: ref, className: cx(namespaceClassNames("select"), className), ...props, ...element }));
});
export const SelectContent = React.forwardRef(function SelectContent({ className, ...props }, ref) {
    const element = useElement("dropdown");
    return (_jsx(RadixSelect.Content, { ref: ref, className: cx(namespaceClassNames("dropdown", "select-dropdown"), className), ...props, ...element }));
});
export const SelectItem = React.forwardRef(function SelectItem({ className, ...props }, ref) {
    const element = useElement("primaryMenuItem");
    return (_jsx(RadixSelect.Item, { ref: ref, className: cx(namespaceClassNames("menu-item", "select-item"), className), ...props, ...element }));
});
export const Label = React.forwardRef(function Label({ children, className, style = {}, ...props }, ref) {
    const element = useElement("label");
    return (
    // @ts-expect-error: Text props are a complex union type depending on the
    // `as` prop value, which breaks down when spreading in `element`
    _jsx(Text, { as: "label", ref: ref, weight: "bold", size: "2", className: cx(namespaceClassNames("label"), className), style: { width: "fit-content", ...style }, ...props, ...element, children: children }));
});
export const TextField = React.forwardRef(function TextField({ className, ...props }, ref) {
    const element = useElement("textfield");
    return (_jsx(RadixTextField.Root, { ref: ref, variant: "surface", className: cx(namespaceClassNames("text-field"), className), ...props, ...element }));
});
export const TextFieldSlot = React.forwardRef(function TextFieldSlot({ className, ...props }, ref) {
    return (_jsx(RadixTextField.Slot, { ref: ref, className: cx(namespaceClassNames("text-field-slot"), className), ...props }));
});
export const PasswordField = React.forwardRef(function TextField({ className, ...props }, ref) {
    const element = useElement("textfield");
    const [showPassword, setShowPassword] = React.useState(false);
    return (_jsx(RadixTextField.Root, { ref: ref, variant: "surface", className: cx(namespaceClassNames("text-field"), className), ...props, ...element, type: showPassword ? "text" : "password", children: _jsx(RadixTextField.Slot, { side: "right", children: _jsx(IconButton, { type: "button", size: "1", variant: "ghost", color: "gray", onClick: () => setShowPassword(!showPassword), children: showPassword ? _jsx(EyeOpenIcon, {}) : _jsx(EyeClosedIcon, {}) }) }) }));
});
export const Badge = React.forwardRef(function Badge({ className, ...props }, ref) {
    const element = useElement("badge");
    return (_jsx(RadixBadge, { ref: ref, className: cx(namespaceClassNames("badge"), className), ...element, ...props }));
});
export const DropdownMenuContent = React.forwardRef(function DropdownMenuContent({ className, ...props }, ref) {
    const element = useElement("dropdown");
    return (_jsx(RadixDropdownMenu.Content, { ref: ref, className: cx(namespaceClassNames("dropdown"), className), ...props, ...element }));
});
export const PrimaryMenuItem = React.forwardRef(function PrimaryMenuItem({ className, ...props }, ref) {
    const element = useElement("primaryMenuItem");
    return (_jsx(RadixDropdownMenu.Item, { ref: ref, className: cx(namespaceClassNames("menu-item"), className), ...props, ...element }));
});
export const DestructiveMenuItem = React.forwardRef(function DestructiveMenuItem({ className, ...props }, ref) {
    const element = useElement("destructiveMenuItem");
    return (_jsx(RadixDropdownMenu.Item, { ref: ref, className: cx(namespaceClassNames("menu-item", "menu-item--destructive"), className), color: "red", ...props, ...element }));
});
export const Avatar = React.forwardRef(function Avatar({ dim, className, ...props }, ref) {
    const element = useElement("avatar");
    return (_jsx(RadixAvatar, { ref: ref, color: "gray", className: cx(namespaceClassNames("avatar"), className), ...props, ...element, 
        // TODO: use CSS var instead of hard-coded value for opacity
        style: dim ? { opacity: 0.6, ...props.style } : props.style }));
});
export const DialogContent = React.forwardRef(function DialogContent({ className, ...props }, forwardedRef) {
    const element = useElement("dialog");
    const [node, setNode] = React.useState(null);
    const ref = useComposedRefs(forwardedRef, setNode);
    useDialogOverlayHack(node, {
        className: namespaceClassNames("dialog-overlay"),
        selector: ".rt-DialogOverlay",
    });
    return (_jsx(RadixDialog.Content, { ref: ref, className: cx(namespaceClassNames("dialog"), className), ...props, ...element }));
});
export const AlertDialogContent = React.forwardRef(function AlertDialogContent({ className, ...props }, forwardedRef) {
    const element = useElement("dialog");
    const [node, setNode] = React.useState(null);
    const ref = useComposedRefs(forwardedRef, setNode);
    useDialogOverlayHack(node, {
        className: namespaceClassNames("dialog-overlay"),
        selector: ".rt-AlertDialogOverlay",
    });
    return (_jsx(RadixAlertDialog.Content, { ref: ref, className: cx(namespaceClassNames("dialog"), className), ...props, ...element }));
});
/**
 * HACK: Radix themes does not expose the dialog overlay, but we want consumer
 * to be able to style it with a classname. This will add a classname to the
 * overlay when the dialog content is mounted.
 */
function useDialogOverlayHack(node, { className, selector }) {
    useLayoutEffect(() => {
        if (!node) {
            return;
        }
        const document = node.ownerDocument;
        const overlay = document.querySelector(selector);
        overlay?.classList.add(className);
    }, [node, className, selector]);
}
//# sourceMappingURL=elements.js.map